import type { CreateInviteResponse, InvitePreviewResponse } from '@mm/shared';
import { apiFetch, type User } from './client';

export async function createStudentInvite(studentId: string): Promise<CreateInviteResponse> {
  const res = await apiFetch(`/students/${studentId}/invite`, { method: 'POST' });
  if (!res.ok) throw new Error(`createStudentInvite ${res.status}`);
  return res.json();
}

export type InvitePreviewResult =
  | { kind: 'ok'; preview: InvitePreviewResponse }
  | { kind: 'not_found' }
  | { kind: 'network_error' };

export async function fetchInvitePreview(token: string): Promise<InvitePreviewResult> {
  try {
    const res = await fetch(`/api/invites/${encodeURIComponent(token)}`);
    if (res.status === 404) return { kind: 'not_found' };
    if (!res.ok) return { kind: 'network_error' };
    const preview = (await res.json()) as InvitePreviewResponse;
    return { kind: 'ok', preview };
  } catch {
    return { kind: 'network_error' };
  }
}

export type InviteAcceptResult =
  | { kind: 'ok'; accessToken: string; user: User }
  | { kind: 'expired' }
  | { kind: 'consumed' }
  | { kind: 'not_found' }
  | { kind: 'invalid_body' }
  | { kind: 'rate_limited' }
  | { kind: 'network_error' };

export async function acceptInvite(token: string, password: string): Promise<InviteAcceptResult> {
  try {
    const res = await fetch(`/api/invites/${encodeURIComponent(token)}/accept`, {
      method: 'POST',
      headers: { 'content-type': 'application/json' },
      credentials: 'include',
      body: JSON.stringify({ password }),
    });
    if (res.status === 404) return { kind: 'not_found' };
    if (res.status === 400) return { kind: 'invalid_body' };
    if (res.status === 429) return { kind: 'rate_limited' };
    if (res.status === 410) {
      const body = (await res.json().catch(() => ({}))) as { error?: string };
      return body.error === 'consumed' ? { kind: 'consumed' } : { kind: 'expired' };
    }
    if (!res.ok) return { kind: 'network_error' };
    const data = (await res.json()) as { accessToken: string; user: User };
    return { kind: 'ok', accessToken: data.accessToken, user: data.user };
  } catch {
    return { kind: 'network_error' };
  }
}
